/**
 * The colour control below the viewport.
 *
 * A row of standard colours for the common case, and behind the last button a popover with
 * hue, saturation and lightness sliders for everything else.
 *
 * That popover replaced an `<input type="color">`, which was the obvious choice and turned
 * out to be the wrong one. That element has no markup of its own: it asks the platform for
 * a picker dialog. Desktop browsers have one; the Meta Quest Browser does not, so in a
 * headset — where this application is meant to be used — pressing it did nothing whatever,
 * with no error and no clue. Everything here is drawn by the page instead, so it works
 * wherever the rest of the page works.
 *
 * Sliders rather than the usual gradient square for the same reason: a headset points at
 * the screen with a controller from a metre away, and a one-dimensional target is far
 * easier to hit that way than a two-dimensional one.
 *
 * The whole control only exists while a model is on screen — there is nothing to colour
 * otherwise, and a control that cannot do anything is worse than no control.
 */

import {
  colourOf,
  describeColour,
  hexToHsl,
  hslToHex,
  normaliseColour,
  STANDARD_COLOURS,
  type Hsl,
} from '../domain/colour.js';
import { hasFileColours } from '../domain/modelFile.js';
import type { ModelId, ModelMeta } from '../domain/types.js';
import { buttonElement, element, inputElement } from './dom.js';

/**
 * How long after the last change the colour is written to storage.
 *
 * Dragging a slider fires a continuous stream of changes. Rendering each one is free;
 * writing each one to IndexedDB is not, so the render keeps up live and the save waits for
 * the user to settle.
 */
const SAVE_DELAY_MS = 200;

export interface ColourPickerOptions {
  /** Show this colour now. Called for every intermediate value while choosing. */
  readonly onPreview: (colour: string) => void;
  /**
   * Keep this colour. Called once the choice has settled.
   *
   * The model id is passed rather than assumed, because a save can still be pending when
   * the displayed model changes — and it belongs to the model that was on screen when it
   * was chosen, not to whichever one is on screen when it lands.
   */
  readonly onCommit: (id: ModelId, colour: string) => void;
}

export class ColourPicker {
  private readonly root = element('colour-picker');
  private readonly swatchRow = element('colour-swatches');
  private readonly more = buttonElement('colour-more');
  private readonly popover = element('colour-popover');
  private readonly locked = element('colour-locked');
  private readonly preview = element('colour-preview');
  private readonly readout = element('colour-readout');
  private readonly hue = inputElement('colour-hue');
  private readonly saturation = inputElement('colour-saturation');
  private readonly lightness = inputElement('colour-lightness');

  private readonly buttons = new Map<string, HTMLButtonElement>();

  private modelId: ModelId | undefined;
  private current = colourOf({});
  /** True when `current` differs from what storage holds. */
  private unsaved = false;
  private saveTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(private readonly options: ColourPickerOptions) {
    this.swatchRow.replaceChildren(
      ...STANDARD_COLOURS.map(({ name, hex }) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'colour__swatch';
        button.style.backgroundColor = hex;
        button.title = name;
        button.setAttribute('aria-label', name);
        button.setAttribute('aria-pressed', 'false');
        button.addEventListener('click', () => {
          // A standard colour is one decision, not a drag, so it is kept immediately.
          this.apply(hex, 'commit');
        });

        this.buttons.set(hex, button);
        return button;
      }),
    );

    this.more.addEventListener('click', () => {
      this.togglePopover();
    });

    for (const slider of [this.hue, this.saturation, this.lightness]) {
      // `input` fires throughout a drag, `change` when it ends. Both go through the same
      // path: the preview keeps up either way, and the save debounce decides when to
      // write.
      slider.addEventListener('input', () => {
        this.applyFromSliders('preview');
      });
      slider.addEventListener('change', () => {
        this.applyFromSliders('commit');
      });
    }

    // Escape closes the popover, as it would a dialog. Bound to the popover rather than
    // the document so it only applies while the focus is inside it.
    this.popover.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.closePopover();
        this.more.focus();
      }
    });

    this.hide();
  }

  /**
   * Show the picker for the model now on display, or hide it when there is none.
   *
   * Any pending save is flushed first, against the model it was chosen for. Switching
   * models while a colour is still settling must not attribute it to the new one.
   */
  showFor(model: ModelMeta | undefined): void {
    this.saveNow();
    this.closePopover();

    if (!model) {
      this.hide();
      return;
    }

    this.modelId = model.id;
    this.current = colourOf(model);
    this.unsaved = false;
    this.reflect();
    this.setLocked(hasFileColours(model));
    this.root.hidden = false;
  }

  /**
   * Put the controls beyond use for a model whose colours came with the file.
   *
   * Disabled rather than hidden. The row keeps its place so the panel does not reflow
   * around whichever model is selected, and the note says why the buttons do nothing —
   * a control that silently vanished would leave somebody looking for it.
   *
   * `disabled` rather than a class and a guard, because it is the only thing that also
   * takes the buttons out of the tab order and tells a screen reader, and because a
   * headset user pointing from a metre away will press what is in front of them.
   */
  private setLocked(locked: boolean): void {
    this.locked.hidden = !locked;
    this.root.classList.toggle('colour--locked', locked);
    this.more.disabled = locked;

    for (const button of this.buttons.values()) button.disabled = locked;

    if (locked) this.closePopover();
  }

  /** Write any pending colour immediately. */
  flush(): void {
    this.saveNow();
  }

  private hide(): void {
    this.root.hidden = true;
    this.modelId = undefined;
  }

  private togglePopover(): void {
    if (this.popover.hidden) {
      this.popover.hidden = false;
      this.more.setAttribute('aria-expanded', 'true');
      document.addEventListener('pointerdown', this.closeOnOutsidePress, true);
      this.hue.focus();
    } else {
      this.closePopover();
    }
  }

  private closePopover(): void {
    if (this.popover.hidden) return;

    this.popover.hidden = true;
    this.more.setAttribute('aria-expanded', 'false');
    document.removeEventListener('pointerdown', this.closeOnOutsidePress, true);

    // Closing settles the choice, so it does not sit unsaved until something else
    // happens to flush it.
    this.saveNow();
  }

  /**
   * Dismiss the popover when something outside it is pressed.
   *
   * On the capture phase, so a press on the canvas closes the popover before the orbit
   * controls start a drag with it still open.
   */
  private readonly closeOnOutsidePress = (event: Event): void => {
    const target = event.target;
    if (target instanceof Node && this.popoverArea().contains(target)) return;
    this.closePopover();
  };

  /** The popover and the button that opens it, which must not count as "outside". */
  private popoverArea(): HTMLElement {
    return this.more.parentElement ?? this.popover;
  }

  private applyFromSliders(intent: 'preview' | 'commit'): void {
    this.apply(hslToHex(this.readSliders()), intent);
  }

  private readSliders(): Hsl {
    return {
      hue: Number(this.hue.value),
      saturation: Number(this.saturation.value),
      lightness: Number(this.lightness.value),
    };
  }

  private apply(value: string, intent: 'preview' | 'commit'): void {
    if (this.modelId === undefined) return;

    const colour = normaliseColour(value);
    if (colour === undefined) return;

    if (colour !== this.current) {
      this.current = colour;
      this.unsaved = true;
      this.reflect();
      this.options.onPreview(colour);
    }

    if (intent === 'commit') {
      this.saveNow();
    } else if (this.unsaved) {
      this.scheduleSave();
    }
  }

  private scheduleSave(): void {
    if (this.saveTimer !== undefined) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => {
      this.saveTimer = undefined;
      this.saveNow();
    }, SAVE_DELAY_MS);
  }

  private saveNow(): void {
    if (this.saveTimer !== undefined) {
      clearTimeout(this.saveTimer);
      this.saveTimer = undefined;
    }

    // Guarded on `unsaved` rather than called unconditionally, so that flushing on every
    // model change does not write a record nobody has touched.
    if (!this.unsaved || this.modelId === undefined) return;

    this.unsaved = false;
    this.options.onCommit(this.modelId, this.current);
  }

  /** Bring every control into line with the current colour. */
  private reflect(): void {
    const name = describeColour(this.current);

    this.more.style.backgroundColor = this.current;
    this.more.title = `More colours (${name})`;

    this.preview.style.backgroundColor = this.current;
    this.readout.textContent = this.current.toUpperCase();

    for (const [hex, button] of this.buttons) {
      button.setAttribute('aria-pressed', String(hex === this.current));
      button.classList.toggle('colour__swatch--active', hex === this.current);
    }

    this.reflectSliders(hexToHsl(this.current));
  }

  /**
   * Move the sliders, and repaint their tracks to show what each one does from here.
   *
   * The saturation and lightness gradients depend on the other two components, so they
   * are rebuilt on every change rather than fixed in the stylesheet — which is what makes
   * the sliders readable as a picker rather than as three anonymous bars.
   */
  private reflectSliders({ hue, saturation, lightness }: Hsl): void {
    // Written straight to `value` rather than through a change event, so setting them
    // here cannot loop back round into `apply`.
    this.hue.value = String(hue);
    this.saturation.value = String(saturation);
    this.lightness.value = String(lightness);

    this.saturation.style.setProperty(
      '--track',
      `linear-gradient(to right, ${hslToHex({ hue, saturation: 0, lightness })}, ${hslToHex(
        { hue, saturation: 100, lightness },
      )})`,
    );

    this.lightness.style.setProperty(
      '--track',
      `linear-gradient(to right, #000000, ${hslToHex({
        hue,
        saturation,
        lightness: 50,
      })}, #ffffff)`,
    );

    this.hue.setAttribute('aria-valuetext', `${String(hue)} degrees`);
  }
}
